import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

// ============ TYPES ============

export interface AnnouncementMedia {
  type: "image" | "video";
  url: string;
  altText?: string;
  posterUrl?: string; // For video poster/thumbnail
}

export interface AnnouncementCTA {
  text: string;
  navigateTo?: string; // Screen ID or URL
  isExternal?: boolean; // If true, opens in new tab
  style?: "primary" | "secondary" | "outline";
}

export interface AnnouncementData {
  id: string;
  type: "welcome" | "feature" | "promotion" | "announcement" | "update";
  title: string;
  subtitle?: string;
  description?: string;
  media?: AnnouncementMedia;
  primaryCta?: AnnouncementCTA;
  secondaryCta?: AnnouncementCTA;
  display?: {
    showOncePerDay?: boolean; // Show once per day
    showOnceEver?: boolean; // Show only once ever
    startDate?: string; // ISO date string
    endDate?: string; // ISO date string
  };
  styling?: {
    backgroundColor?: string;
    textColor?: string;
    accentColor?: string;
  };
  priority?: number; // Higher priority shows first
  enabled?: boolean;
}

interface AnnouncementModalProps {
  announcement: AnnouncementData | null;
  onClose: () => void;
  onNavigate?: (screenId: string) => void;
  userName?: string; // User's name for personalized greeting
}

// ============ STORAGE KEYS ============

const ANNOUNCEMENT_STORAGE_KEY = "garage360-announcements-shown";

interface AnnouncementShownData {
  [announcementId: string]: {
    lastShownDate: string; // ISO date string
    shownCount: number;
  };
}

// ============ HELPER FUNCTIONS ============

const getAnnouncementShownData = (): AnnouncementShownData => {
  try {
    const stored = localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveAnnouncementShownData = (data: AnnouncementShownData): void => {
  try {
    localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

export const markAnnouncementAsShown = (announcementId: string): void => {
  const data = getAnnouncementShownData();
  const today = new Date().toISOString().split("T")[0];

  data[announcementId] = {
    lastShownDate: today,
    shownCount: (data[announcementId]?.shownCount || 0) + 1,
  };

  saveAnnouncementShownData(data);
};

export const shouldShowAnnouncement = (announcement: AnnouncementData): boolean => {
  if (!announcement.enabled) return false;

  const today = new Date().toISOString().split("T")[0];
  const now = new Date();

  // Check date range
  if (announcement.display?.startDate) {
    const startDate = new Date(announcement.display.startDate);
    if (now < startDate) return false;
  }

  if (announcement.display?.endDate) {
    const endDate = new Date(announcement.display.endDate);
    if (now > endDate) return false;
  }

  const data = getAnnouncementShownData();
  const shownData = data[announcement.id];

  // Show once ever
  if (announcement.display?.showOnceEver) {
    return !shownData || shownData.shownCount === 0;
  }

  // Show once per day
  if (announcement.display?.showOncePerDay) {
    return !shownData || shownData.lastShownDate !== today;
  }

  // Default: show always
  return true;
};

// ============ TYPE BADGES ============

const getTypeBadge = (type: AnnouncementData["type"]) => {
  const badges = {
    welcome: { text: "Welcome", bg: "bg-green-100 dark:bg-green-900/50", color: "text-green-800 dark:text-green-300", icon: "👋" },
    feature: { text: "New Feature", bg: "bg-blue-100 dark:bg-blue-900/50", color: "text-blue-800 dark:text-blue-300", icon: "✨" },
    promotion: { text: "Special Offer", bg: "bg-orange-100 dark:bg-orange-900/50", color: "text-orange-800 dark:text-orange-300", icon: "🎁" },
    announcement: { text: "Announcement", bg: "bg-purple-100 dark:bg-purple-900/50", color: "text-purple-800 dark:text-purple-300", icon: "📢" },
    update: { text: "Update", bg: "bg-cyan-100 dark:bg-cyan-900/50", color: "text-cyan-800 dark:text-cyan-300", icon: "🚀" },
  };
  return badges[type] || badges.announcement;
};

// ============ COMPONENT ============

export const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  announcement,
  onClose,
  onNavigate,
  userName,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  // Get personalized title for welcome type
  const getPersonalizedTitle = () => {
    if (!announcement) return "";

    // For welcome type, add user's name
    if (announcement.type === "welcome" && userName) {
      // Check if title already contains a greeting pattern
      const greetingPatterns = /^(Welcome|Hello|Hi|Hey|Good morning|Good afternoon|Good evening)/i;
      if (greetingPatterns.test(announcement.title)) {
        // Insert name after the greeting word
        return announcement.title.replace(
          greetingPatterns,
          `$1, ${userName}`
        );
      }
      // Default: prepend "Hi, {name}! " to title
      return `Hi, ${userName}! ${announcement.title}`;
    }

    return announcement.title;
  };

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  // Animate in on mount
  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [announcement]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      if (announcement) {
        markAnnouncementAsShown(announcement.id);
      }
      onClose();
    }, 300);
  }, [announcement, onClose]);

  // Handle CTA click
  const handleCtaClick = useCallback((cta: AnnouncementCTA) => {
    if (cta.isExternal && cta.navigateTo) {
      window.open(cta.navigateTo, "_blank");
      handleClose();
    } else if (cta.navigateTo && onNavigate) {
      const targetScreen = cta.navigateTo;
      // For "start-tour", close modal without calling onClose (which dispatches dismissal event)
      if (targetScreen === "start-tour") {
        setIsVisible(false);
        setTimeout(() => {
          if (announcement) {
            markAnnouncementAsShown(announcement.id);
          }
          // Call onNavigate directly without calling onClose to avoid closing the tour
          onNavigate(targetScreen);
        }, 300);
      } else {
        onNavigate(targetScreen);
        handleClose();
      }
    } else {
      handleClose();
    }
  }, [onNavigate, handleClose, announcement]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  if (!announcement) return null;

  const badge = getTypeBadge(announcement.type);
  const hasMedia = announcement.media && announcement.media.url;

  return createPortal(
    <div
      className={`fixed inset-0 z-[100002] flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? "opacity-100" : "opacity-0"
        }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="announcement-title"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"
          }`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 ${isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
          }`}
        style={{
          backgroundColor: announcement.styling?.backgroundColor,
          color: announcement.styling?.textColor,
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/20 dark:bg-white/20 text-white hover:bg-black/40 dark:hover:bg-white/40 transition-colors backdrop-blur-sm"
          aria-label="Close announcement"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Media Section */}
        {hasMedia && (
          <div className="relative w-full aspect-video bg-gray-900 overflow-hidden">
            {announcement.media!.type === "video" ? (
              <>
                {/* Video Loading Placeholder */}
                {isVideoLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/30 border-t-white"></div>
                  </div>
                )}
                <video
                  src={announcement.media!.url}
                  poster={announcement.media!.posterUrl}
                  className="w-full h-full object-cover"
                  controls={isVideoPlaying}
                  autoPlay={false}
                  playsInline
                  onLoadedData={() => setIsVideoLoading(false)}
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                />
                {/* Play Button Overlay */}
                {!isVideoPlaying && !isVideoLoading && (
                  <button
                    onClick={() => {
                      const video = document.querySelector("video");
                      if (video) video.play();
                    }}
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                  >
                    <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <svg className="w-10 h-10 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </button>
                )}
              </>
            ) : (
              <img
                src={announcement.media!.url}
                alt={announcement.media!.altText || announcement.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Hide image on error
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}

            {/* Gradient Overlay for type badge */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

            {/* Type Badge on Media */}
            <div className="absolute bottom-3 left-3">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.color} backdrop-blur-sm`}>
                <span>{badge.icon}</span>
                {badge.text}
              </span>
            </div>
          </div>
        )}

        {/* Content Section */}
        <div className="p-6">
          {/* Type Badge (if no media) */}
          {!hasMedia && (
            <div className="mb-4">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.color}`}>
                <span>{badge.icon}</span>
                {badge.text}
              </span>
            </div>
          )}

          {/* Personalized Welcome Greeting for welcome type */}
          {announcement.type === "welcome" && userName && (
            <p className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1 flex items-center gap-2">
              <span className="text-lg">👋</span>
              {getTimeBasedGreeting()}, <span className="font-bold">{userName}</span>!
            </p>
          )}

          {/* Title */}
          <h2
            id="announcement-title"
            className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2"
          >
            {announcement.title}
          </h2>

          {/* Subtitle */}
          {announcement.subtitle && (
            <p className="text-lg text-primary-600 dark:text-primary-400 font-medium mb-3">
              {announcement.subtitle}
            </p>
          )}

          {/* Description */}
          {announcement.description && (
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6">
              {announcement.description}
            </p>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {announcement.primaryCta && (
              <button
                onClick={() => handleCtaClick(announcement.primaryCta!)}
                className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-all hover:shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
                style={{ backgroundColor: announcement.styling?.accentColor }}
              >
                {announcement.primaryCta.text}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            )}

            {announcement.secondaryCta && (
              <button
                onClick={() => handleCtaClick(announcement.secondaryCta!)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all active:scale-[0.98]"
              >
                {announcement.secondaryCta.text}
              </button>
            )}
          </div>

          {/* Skip Link */}
          <button
            onClick={handleClose}
            className="w-full mt-4 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AnnouncementModal;
